(() => {
  const cleanups = new WeakMap();
  const snapshots = new WeakMap();
  const pending = new Set();
  let pendingFrame;
  const managedSelector = '[data-pr-carousel],[data-pr-track],[data-pr-prev],[data-pr-next],[data-pr-dots],.rc-card,[data-pr-strip],.strip-track,.strip-item,[data-pr-accordion],.faq details,[data-pr-reviews],.rv-grid,.rv-card,[data-pr-load-more],[data-pr-review-row],.pr-card,[data-pr-review-dots]';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const strings = () => window.PureRevival?.translations?.sections || window.prSectionStrings || { slide: 'Go to slide {number}' };
  const scrollToCard = (track, card) => {
    const first = track.querySelector('.rc-card');
    if (!card || !first) return;
    track.scrollTo({ left: card.offsetLeft - first.offsetLeft, behavior: reduced.matches ? 'instant' : 'smooth' });
  };
  function carousel(root) {
    const track = root.querySelector('[data-pr-track]');
    if (!track) return () => {};
    const cards = [...track.querySelectorAll(':scope > .rc-card')];
    const previous = root.querySelector('[data-pr-prev]');
    const next = root.querySelector('[data-pr-next]');
    const dotsRoot = root.querySelector('[data-pr-dots]');
    const controller = new AbortController();
    const { signal } = controller;
    let index = 0;
    let timer;
    let paused = false;
    let frame;
    let dots = [];
    function positions() {
      const first = cards[0]?.offsetLeft || 0;
      return cards.map(card => Math.min(card.offsetLeft - first, track.scrollWidth - track.clientWidth));
    }
    function update() {
      const all = positions();
      index = all.reduce((best, value, current) => Math.abs(value - track.scrollLeft) < Math.abs(all[best] - track.scrollLeft) ? current : best, 0);
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
      dots.forEach((dot, i) => {
        dot.classList.toggle('on', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        dot.hidden = i > 0 && all[i] === all[i - 1];
      });
    }
    function move(direction) {
      const all = positions();
      const target = direction > 0 ? all.findIndex(value => value > track.scrollLeft + 2) : all.findLastIndex(value => value < track.scrollLeft - 2);
      if (target >= 0) scrollToCard(track, cards[target]);
    }
    previous?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });
    if (dotsRoot) {
      dotsRoot.replaceChildren();
      dots = cards.map((card, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rc-dot';
        dot.setAttribute('aria-label', strings().slide.replace('{{ number }}', i + 1).replace('{number}', i + 1));
        dot.addEventListener('click', () => scrollToCard(track, card), { signal });
        dotsRoot.append(dot);
        return dot;
      });
    }
    track.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); }, { signal, passive: true });
    root.addEventListener('mouseenter', () => { paused = true; }, { signal });
    root.addEventListener('mouseleave', () => { paused = false; }, { signal });
    root.addEventListener('focusin', () => { paused = true; }, { signal });
    root.addEventListener('focusout', event => { if (!root.contains(event.relatedTarget)) paused = false; }, { signal });
    root.addEventListener('shopify:block:select', event => { const card = event.target.closest('.rc-card') || event.target.querySelector('.rc-card'); if (card) scrollToCard(track, card); }, { signal });
    if (root.dataset.autoplay === 'true' && !reduced.matches) {
      timer = setInterval(() => {
        if (paused || document.hidden || !root.getClientRects().length) return;
        const rect = root.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > innerHeight) return;
        if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 2) scrollToCard(track, cards[0]);
        else move(1);
      }, Math.max(3, Number(root.dataset.interval) || 6) * 1000);
    }
    const observer = new ResizeObserver(update);
    observer.observe(track);
    update();
    return () => { controller.abort(); observer.disconnect(); clearInterval(timer); cancelAnimationFrame(frame); };
  }
  function strip(root) {
    const track = root.querySelector('.strip-track');
    if (!track) return () => {};
    let observer;
    let frame;
    const originals = [...track.querySelectorAll(':scope > .strip-item')].filter(child => !child.hasAttribute('data-pr-clone'));
    if (root.dataset.motion === 'marquee') {
      function rebuild() {
        track.querySelectorAll('[data-pr-clone]').forEach(clone => clone.remove());
        const first = originals[0];
        const last = originals.at(-1);
        if (!first || !last) return;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const width = last.offsetLeft - first.offsetLeft + last.offsetWidth + gap;
        const copies = Math.max(1, Math.ceil(root.clientWidth / Math.max(1, width)));
        for (let repeat = 0; repeat < copies + 1; repeat++) {
          originals.forEach(item => {
            track.append(cloneOf(item));
          });
        }
        track.style.setProperty('--pr-strip-distance', `${width}px`);
      }
      observer = new ResizeObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(rebuild); });
      observer.observe(root);
      rebuild();
    }
    return () => { observer?.disconnect(); cancelAnimationFrame(frame); track.querySelectorAll('[data-pr-clone]').forEach(clone => clone.remove()); };
  }
  function cloneOf(item) {
    const clone = item.cloneNode(true);
    clone.setAttribute('data-pr-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
    clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
    return clone;
  }
  // Review cards under the customer photos: on laptops they loop sideways (copies follow the originals);
  // on phones the copies are hidden, the cards swipe and the dots follow the card at the left edge.
  function reviewRow(root) {
    const scroller = root.querySelector('.pr-rev');
    const track = root.querySelector('.pr-track');
    const dotsRoot = root.querySelector('[data-pr-review-dots]');
    if (!scroller || !track) return () => {};
    const cards = [...track.querySelectorAll(':scope > .pr-card')].filter(card => !card.hasAttribute('data-pr-clone'));
    const controller = new AbortController();
    const { signal } = controller;
    let markFrame;
    let sizeFrame;
    let dots = [];
    function rebuild() {
      track.querySelectorAll('[data-pr-clone]').forEach(clone => clone.remove());
      const first = cards[0];
      const last = cards.at(-1);
      if (!first || !last) return;
      const width = last.offsetLeft - first.offsetLeft + last.offsetWidth + (parseFloat(getComputedStyle(last).marginRight) || 0);
      const copies = Math.max(1, Math.ceil(scroller.clientWidth / Math.max(1, width)));
      for (let repeat = 0; repeat < copies; repeat++) cards.forEach(card => track.append(cloneOf(card)));
      track.style.setProperty('--pr-rev-distance', `${width}px`);
    }
    const inset = () => parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0;
    const offset = card => card.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    function mark() {
      markFrame = undefined;
      if (!cards.length) return;
      const index = cards.reduce((best, card, i) => Math.abs(offset(card) - inset()) < Math.abs(offset(cards[best]) - inset()) ? i : best, 0);
      dots.forEach((dot, i) => {
        dot.classList.toggle('on', i === index);
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
    function go(card) {
      scroller.scrollTo({ left: scroller.scrollLeft + offset(card) - inset(), behavior: reduced.matches ? 'instant' : 'smooth' });
    }
    if (dotsRoot) {
      dotsRoot.replaceChildren();
      dots = cards.map((card, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pr-dot';
        dot.setAttribute('aria-label', strings().slide.replace('{{ number }}', i + 1).replace('{number}', i + 1));
        dot.addEventListener('click', () => go(card), { signal });
        dotsRoot.append(dot);
        return dot;
      });
    }
    scroller.addEventListener('scroll', () => { if (!markFrame) markFrame = requestAnimationFrame(mark); }, { signal, passive: true });
    root.addEventListener('shopify:block:select', event => {
      const card = cards.find(item => item === event.target || item.contains(event.target));
      if (card) go(card);
    }, { signal });
    const observer = new ResizeObserver(() => { cancelAnimationFrame(sizeFrame); sizeFrame = requestAnimationFrame(() => { rebuild(); mark(); }); });
    observer.observe(scroller);
    rebuild();
    mark();
    return () => {
      controller.abort();
      observer.disconnect();
      cancelAnimationFrame(markFrame);
      cancelAnimationFrame(sizeFrame);
      track.querySelectorAll('[data-pr-clone]').forEach(clone => clone.remove());
      track.style.removeProperty('--pr-rev-distance');
      dotsRoot?.replaceChildren();
    };
  }
  // Neighbouring sections with "Join background" on share one gradient (What's inside and the comparison table).
  let blendFrame;
  const blendObserver = new ResizeObserver(() => scheduleBlend());
  function blendSections() {
    blendFrame = undefined;
    const outer = el => el.closest('.shopify-section') || el;
    const groups = [];
    document.querySelectorAll('[data-pr-blend]').forEach(el => {
      const group = groups.at(-1);
      if (group && outer(group.at(-1)).nextElementSibling === outer(el)) group.push(el);
      else groups.push([el]);
    });
    groups.forEach(group => {
      const top = group[0].getBoundingClientRect().top;
      const total = group.at(-1).getBoundingClientRect().bottom - top;
      group.forEach(el => {
        if (group.length < 2) {
          ['background-size', 'background-position', 'background-repeat'].forEach(name => el.style.removeProperty(name));
          return;
        }
        el.style.backgroundSize = `100% ${total}px`;
        el.style.backgroundPosition = `0 ${top - el.getBoundingClientRect().top}px`;
        el.style.backgroundRepeat = 'no-repeat';
      });
    });
  }
  function scheduleBlend() {
    if (!blendFrame) blendFrame = requestAnimationFrame(blendSections);
  }
  function watchBlend() {
    blendObserver.disconnect();
    document.querySelectorAll('[data-pr-blend]').forEach(el => blendObserver.observe(el));
    scheduleBlend();
  }
  function accordion(root) {
    const controller = new AbortController();
    const rows = [...root.querySelectorAll('details')];
    rows.forEach(row => row.addEventListener('toggle', () => {
      if (root.dataset.single !== 'true' || !row.open) return;
      rows.forEach(other => { if (other !== row) other.open = false; });
    }, { signal: controller.signal }));
    root.addEventListener('shopify:block:select', event => {
      const row = event.target.closest('details') || event.target.querySelector('details');
      if (row) row.open = true;
    }, { signal: controller.signal });
    return () => controller.abort();
  }
  function reviews(root) {
    const count = Number(root.dataset.pageSize);
    const grid = root.querySelector('.rv-grid');
    if (!grid) return () => {};
    const cards = [...grid.children].filter(child => child.tagName !== 'STYLE');
    const button = root.querySelector('[data-pr-load-more]');
    if (!count || !button) return () => {};
    let shown = count;
    const controller = new AbortController();
    function update() {
      cards.forEach((card, i) => { card.hidden = i >= shown; });
      button.hidden = shown >= cards.length;
    }
    button.addEventListener('click', () => { shown += count; update(); }, { signal: controller.signal });
    root.addEventListener('shopify:block:select', event => {
      const index = cards.findIndex(card => card === event.target || card.contains(event.target) || event.target.contains(card));
      if (index >= shown) { shown = index + 1; update(); }
    }, { signal: controller.signal });
    update();
    return () => { controller.abort(); cards.forEach(card => { card.hidden = false; }); };
  }
  function reconcile(section, force = false) {
    if (!section.isConnected) {
      cleanups.get(section)?.();
      cleanups.delete(section);
      snapshots.delete(section);
      return;
    }
    const nodes = [...section.querySelectorAll(managedSelector)].filter(node => !node.closest('[data-pr-clone]'));
    section.querySelectorAll('.rv-grid').forEach(grid => nodes.push(...[...grid.children].filter(node => node.tagName !== 'STYLE')));
    const config = nodes.map(node => [node.dataset.autoplay, node.dataset.interval, node.dataset.motion, node.dataset.single, node.dataset.pageSize].join('|')).join(';');
    const previous = snapshots.get(section);
    if (!force && previous?.config === config && previous.nodes.length === nodes.length && previous.nodes.every((node, i) => node === nodes[i])) return;
    snapshots.set(section, { nodes, config });
    cleanups.get(section)?.();
    const cleanup = [];
    section.querySelectorAll('[data-pr-carousel]').forEach(el => cleanup.push(carousel(el)));
    section.querySelectorAll('[data-pr-strip]').forEach(el => cleanup.push(strip(el)));
    section.querySelectorAll('[data-pr-accordion]').forEach(el => cleanup.push(accordion(el)));
    section.querySelectorAll('[data-pr-reviews]').forEach(el => cleanup.push(reviews(el)));
    section.querySelectorAll('[data-pr-review-row]').forEach(el => cleanup.push(reviewRow(el)));
    cleanups.set(section, () => cleanup.forEach(fn => fn()));
  }
  function init(container = document, force = false) {
    const sections = [...container.querySelectorAll('[data-pr-section]')];
    if (container.matches?.('[data-pr-section]')) sections.unshift(container);
    sections.forEach(section => reconcile(section, force));
    watchBlend();
  }
  function queue(section) {
    if (!section) return;
    pending.add(section);
    if (pendingFrame) return;
    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = undefined;
      const sections = [...pending];
      pending.clear();
      sections.forEach(section => reconcile(section));
    });
  }
  function start() {
    const observer = new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'attributes') {
          queue(record.target.closest?.('[data-pr-section]'));
          return;
        }
        const changed = [...record.addedNodes, ...record.removedNodes].filter(node => node.nodeType === 1 && !node.closest('[data-pr-clone]'));
        const relevant = changed.some(node => record.target.matches?.('.rv-grid') || node.matches(managedSelector + ',[data-pr-section],details') || node.querySelector(managedSelector + ',[data-pr-section],details'));
        if (!relevant) return;
        queue(record.target.closest?.('[data-pr-section]'));
        changed.forEach(node => {
          if (node.matches('[data-pr-section]')) queue(node);
          node.querySelectorAll('[data-pr-section]').forEach(queue);
          if (node.matches('[data-pr-section]') || node.querySelector('[data-pr-section]')) watchBlend();
        });
      });
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-autoplay', 'data-interval', 'data-motion', 'data-single', 'data-page-size'] });
    init();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', event => init(event.target, true));
  document.addEventListener('shopify:section:reorder', watchBlend);
  document.addEventListener('shopify:section:unload', event => {
    const sections = [...event.target.querySelectorAll('[data-pr-section]')];
    if (event.target.matches?.('[data-pr-section]')) sections.unshift(event.target);
    sections.forEach(section => { cleanups.get(section)?.(); cleanups.delete(section); snapshots.delete(section); });
    requestAnimationFrame(watchBlend);
  });
})();
